import React from 'react';
import './CalculusOfMeaning.css';

function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

function CalculusOfCare() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <video autoPlay muted playsInline className="calculus-hero-video" loop>
          <source src="/assets/calculus-hero.mp4" type="video/mp4" />
        </video>
        <div className="calculus-hero-overlay" />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">Systems Capacity &middot; Neurochemistry &middot; The Cost of Compassion</span>
          <h1>The Calculus of Care</h1>
          <p className="calculus-hero-sub">Care is not infinite. It is a neurochemical resource with a production rate, a consumption rate, and a balance that goes to zero when nobody is watching.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <div className="calculus-content">

          {/* The New CNA */}
          <section className="calc-section reveal">
            <h2>The New CNA</h2>
            <p className="calc-lead">
              She took the job because she needed work. She passed the certification because she
              studied. She knows how to take vitals, how to transfer, how to change a brief, how
              to document. She is competent. She is prepared. She is not ready for what happens
              next.
            </p>
            <p>
              There is no orientation. There is supposed to be one. The handbook says two weeks
              of supervised shifts with a preceptor who walks her through the residents, their
              conditions, their preferences, their triggers. The handbook says she will learn
              who needs to be turned every two hours and who will scream if you touch their left
              shoulder and who forgets where they are every eleven minutes and who has family
              that visits and who has family that doesn't.
            </p>
            <p>
              The handbook is in a binder on a shelf in an office she has never entered.
            </p>
            <p>
              What happens instead: she walks through the door at 6:45 AM. The night shift
              nurse looks up, says "you're the new one?" and hands her a hall. Fourteen
              residents. She does not know their names. She does not know their diagnoses. She
              does not know who is a fall risk and who is combative and who has dementia and
              who is lucid and lonely and waiting for someone to say good morning.
            </p>
            <p>
              She is told to start rounds.
            </p>
          </section>

          <Pulse />

          {/* Her Chemistry */}
          <section className="calc-section reveal">
            <h2>Her Chemistry at 7 AM</h2>
            <p>
              She has not started yet and her cortisol is already elevated. This is not the
              acute cortisol of a crisis. This is the chronic cortisol of a nervous system
              that has been running on stress for so long it no longer remembers baseline.
            </p>

            <div className="calc-table-wrap">
              <table className="calc-table">
                <thead>
                  <tr>
                    <th>Neurochemical</th>
                    <th>State</th>
                    <th>Why</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Cortisol</td><td>Chronically elevated</td><td>New job. No training. Unfamiliar environment. Financial pressure that drove her here. Cortisol has been her dominant hormone for months.</td></tr>
                  <tr><td>Oxytocin</td><td>Baseline low</td><td>She does not know anyone here. No bonding has occurred. No trust has been formed. She is a stranger in a building full of strangers.</td></tr>
                  <tr><td>Dopamine</td><td>Suppressed</td><td>No reward signals. No positive feedback. No sense that effort leads to outcome. She has not been told she is doing well because she has not started.</td></tr>
                  <tr><td>Serotonin</td><td>Depleted</td><td>Chronic cortisol suppresses serotonin production. The steady baseline that enables patience, calm, and "I can handle this" has been chemically eroded before she walks through the door.</td></tr>
                  <tr><td>Adrenaline</td><td>Low-grade hum</td><td>Not a spike. A constant. The body running on emergency power as a default state because it has forgotten what rest power feels like.</td></tr>
                </tbody>
              </table>
            </div>

            <p>
              This is the neurochemical profile of a person who is expected to provide care
              to fourteen human beings, each of whom requires compassion, patience, gentleness,
              and presence. Every one of those qualities is a neurochemical product. Every one
              of them is currently undersupplied.
            </p>

            <div className="calc-thesis-box">
              <p>You cannot pour from an empty cup. That is a metaphor. The neurochemistry is more precise: you cannot produce oxytocin-mediated compassion from a system running on cortisol-dominant chemistry. The substrate is wrong. The precursors are depleted. The production pathway is suppressed. Care is not a choice. It is a chemical capacity. And hers was consumed before she clocked in.</p>
            </div>
          </section>

          <Pulse />

          {/* No Linen */}
          <section className="calc-section reveal">
            <h2>No Linen</h2>
            <p>
              The first resident needs a bath. She knows how to give a bath. She was
              trained. She is competent.
            </p>
            <p>
              There are no towels.
            </p>
            <p>
              She checks the linen closet. Empty. She checks the cart. Empty. She asks the
              nurse. The nurse says the delivery was short this week. Budget cuts. They are
              rationing linen. She can use washcloths, two per resident, and a single sheet
              for drying.
            </p>
            <p>
              Two washcloths. For a full-body bath of a human being who cannot bathe
              themselves. Who is lying in a bed they cannot leave. Who has been waiting since
              yesterday for someone to help them feel clean.
            </p>
            <p>
              She does not know this resident. She does not know if this person likes warm
              water or cool. Does not know if they are afraid of being moved. Does not know
              if they will grab her arm, or hit her, or cry, or say thank you, or say
              nothing at all. She has been given no information about the person she is about
              to touch in the most intimate way one person touches another: washing the body
              of someone who cannot wash their own.
            </p>
            <p>
              She has two washcloths and no towels and no orientation and her cortisol is
              already above sustainable threshold and she has not finished her first resident
              of fourteen.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Resource Deficit Cascade</span>
              <code>if resources &lt; minimum AND training = absent AND cortisol &gt; &theta;_chronic &rarr; care_quality = f(willpower) not f(competence)</code>
              <span className="calc-eq-desc">When the system strips resources below minimum, removes training, and presents a caregiver whose cortisol is already chronic, the quality of care becomes a function of individual willpower rather than professional competence. The system has made competence irrelevant. She knows HOW to give a bath. She cannot give a bath because there are no towels. The failure is upstream of her.</span>
            </div>
          </section>

          <Pulse />

          {/* Nobody Cares */}
          <section className="calc-section reveal">
            <h2>The Staff</h2>
            <p>
              She asks for help. Not because she cannot do the work. Because she does not
              know the residents and she is afraid of hurting someone she knows nothing about.
            </p>
            <p>
              The other CNA on the hall looks at her and says "you'll figure it out."
            </p>
            <p>
              This is not cruelty. Read it again. "You'll figure it out" is not indifference.
              It is the verbal output of a nervous system that figured it out alone, that was
              never oriented either, that learned by making mistakes on real people and
              carrying the guilt of those mistakes in scars that never decayed, and whose
              oxytocin has been so depleted by years of this that the metabolic cost of
              helping one more person exceeds what the chemistry can produce.
            </p>
            <p>
              The nurse does not look up from the med cart. Not because the nurse does not
              care. Because the nurse has twenty-eight residents to medicate before 9 AM
              and the state will fine the facility if a single dose is late and the nurse's
              cortisol is calibrated to the regulatory clock, not the human one.
            </p>
            <p>
              Nobody in this building is failing. The building is failing everyone in it.
            </p>

            <div className="calc-equation">
              <span className="calc-eq-label">Compassion Production Function</span>
              <code>C<sub>available</sub> = C<sub>baseline</sub> &minus; &sum;(cortisol_damage<sub>i</sub>) &minus; time_in_role &middot; depletion_rate + support_received &middot; OT_recovery</code>
              <span className="calc-eq-desc">Available compassion is baseline capacity minus accumulated cortisol damage minus chronic depletion, plus whatever oxytocin recovery is provided by the system. When support_received equals zero, the equation simplifies to: compassion decreases monotonically with time until the person quits or breaks. There is no other trajectory when the recovery term is zero.</span>
            </div>
          </section>

          <Pulse />

          {/* Proportional */}
          <section className="calc-section reveal">
            <h2>Care Is Proportional to Harm</h2>
            <p>
              The Calculus of Harm demonstrated that structural harm accumulates when the
              neurochemistry of every person in a system has been pushed past sustainability.
              The Calculus of Care demonstrates the inverse: the capacity to provide care is
              directly proportional to the harm the system has already done to the caregiver.
            </p>
            <p>
              This is not a correlation. It is a causal relationship. The same cortisol
              that accumulates from structural harm is the cortisol that suppresses the
              oxytocin production required for care. The same serotonin depletion that
              produces resentment in the caregiver is the serotonin depletion that removes
              the patience required for gentle handling. The same dopamine suppression that
              makes a CNA stop believing effort matters is the dopamine suppression that
              makes a CNA stop trying.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">The Proportionality</span>
              <code>Care(t) &prop; 1 / Harm(t)</code>
              <span className="calc-eq-desc">The capacity to provide care is inversely proportional to the accumulated structural harm done to the caregiver. This is not a metaphor. It is pharmacokinetics. Cortisol suppresses oxytocin synthesis. The chemistry that produces harm and the chemistry that produces care share the same substrate. They are antagonistic. You cannot maximize both. The system that harms its caregivers is the system that depletes its own capacity for care.</span>
            </div>

            <p>
              This means every budget cut that increases workload is a care cut. Every
              unfilled position is a care deficit. Every skipped orientation is a care
              risk. Every missing towel is a dignity reduction. And every CNA who quits is
              a care system that consumed its own supply.
            </p>
          </section>

          <Pulse />

          {/* Transfer */}
          <section className="calc-section reveal">
            <h2>The Transfer</h2>
            <p>
              At 10 AM she is told to transfer a resident from bed to wheelchair. She has
              not been told this resident has a contracture on the left side. She has not been
              told this resident screams when the left arm is moved. She has not been told
              the resident weighs 220 pounds and the lift is broken and the facility ordered
              a replacement three weeks ago and it has not arrived.
            </p>
            <p>
              She performs the transfer the way she was trained. She reaches under the left
              arm. The resident screams. The sound is the same frequency as the woman at
              Gate 14 in an airport terminal. It bypasses cognition and plugs directly into
              the brainstem. The new CNA's adrenaline spikes. Her hands release. The resident
              slumps. She catches them. Barely. Her back takes the weight because her arms
              were not positioned correctly because she did not know about the contracture
              because nobody told her.
            </p>
            <p>
              She stands there holding a screaming human being, her lumbar spine compressed
              under 220 pounds, her adrenaline maxed, her cortisol through the ceiling, and
              the other CNA walks past the doorway and does not stop.
            </p>
            <p>
              Nobody stops.
            </p>
            <p>
              She completes the transfer. She is shaking. Her back will hurt for three days.
              She will not report it because she is new and does not want to seem weak and
              does not know that the injury she just sustained is the number one reason CNAs
              leave the field within the first year.
            </p>
            <p>
              Nobody told her that either.
            </p>
          </section>

          <Pulse />

          {/* What AI Could See */}
          <section className="calc-section reveal">
            <h2>What an AI Could See</h2>
            <p>
              Imagine a system that modeled the caregiver, not just the patient.
            </p>

            <div className="calc-implications">
              <div className="calc-implication">
                <h4>Before her first shift: the system flags no orientation completed.</h4>
                <p>A new CNA assigned to a hall with no preceptor, no resident briefing,
                and no documented training creates a predictable care risk. The system
                alerts the charge nurse. Not as a task. As a neurochemical prediction:
                this caregiver will encounter fourteen unknown variables with chronic
                cortisol and no oxytocin anchoring. The probability of an incident is
                calculable.</p>
              </div>
              <div className="calc-implication">
                <h4>At 8 AM: the system detects linen deficit below care threshold.</h4>
                <p>Two washcloths per resident is below the minimum standard for dignified
                bathing. The system does not file a supply request. It recalculates the
                care capacity of the entire hall: insufficient resources reduce care quality
                from competence-driven to willpower-driven. It flags the administrator.</p>
              </div>
              <div className="calc-implication">
                <h4>Before the transfer: the system routes the resident's care profile.</h4>
                <p>Left-side contracture. Weight 220. Lift status: broken, replacement
                pending. This combination plus an unoriented CNA equals an injury
                probability that exceeds acceptable risk. The system does not let the
                transfer proceed without a second pair of hands. Not because of a rule.
                Because the math says someone gets hurt.</p>
              </div>
              <div className="calc-implication">
                <h4>At end of shift: the system models the CNA's neurochemical state.</h4>
                <p>Cortisol sustained above chronic threshold for 12 hours. No oxytocin
                recovery events (no positive feedback, no peer support, no supervisor
                check-in). Serotonin projected below functional baseline. Probability of
                return for next shift: declining. Probability of injury report suppression:
                high. The system alerts HR. Not for discipline. For support.</p>
              </div>
            </div>

            <p>
              None of this requires surveillance. It requires modeling. The neurochemical
              state of the caregiver is as predictable as the neurochemical state of the
              patient. Both follow pharmacokinetic curves. Both have measurable thresholds.
              Both can be tracked, supported, and protected.
            </p>
            <p>
              The difference: every health system in the country monitors the patient. Nobody
              monitors the person providing the care.
            </p>
          </section>

          <Pulse />

          {/* The CNA Knows */}
          <section className="calc-section reveal">
            <h2>What the CNA Knows</h2>
            <p>
              I am a CNA. I have been the new one on the hall with no orientation. I have
              been handed fourteen residents I did not know. I have given baths with
              insufficient linen. I have transferred residents without knowing their
              contractures. I have hurt my back and not reported it. I have walked past a
              room where someone was calling because I was already in a room where someone
              was falling.
            </p>
            <p>
              I have also been the one who stayed. Who came back the next day. Who learned
              the residents by heart because nobody gave her a chart. Who figured out that
              Mrs. Johnson calms down if you sing softly. Who discovered that Mr. Williams
              likes his water at room temperature. Who memorized every contracture, every
              allergy, every fear, every preference, by trial and error and by the scars
              left from the errors.
            </p>
            <p>
              The Calculus of Care says that my capacity to give care is inversely
              proportional to the harm the system has done to me. The math is correct. But
              the math does not account for one variable.
            </p>
            <p>
              Stubbornness.
            </p>
            <p>
              There is a term in pharmacokinetics for a compound that resists the expected
              decay curve. It is called a scar. A memory so intense it resists normal
              half-life dynamics. The first time a resident grabs your hand and says "thank
              you for being here," the oxytocin spike scars. Permanently. And that scar
              sits underneath the cortisol, underneath the depletion, underneath the budget
              cuts and the missing towels and the broken lifts. It sits there and it says:
              this is why you stay.
            </p>
            <p>
              The system consumes your chemistry. The resident replenishes it. That is the
              calculus of care. The system takes. The human gives. And the CNA stands
              between them, metabolizing both, running on scars.
            </p>
          </section>

          <Pulse />

          {/* Closing */}
          <section className="calc-section calc-closing reveal">
            <p className="calc-closing-line"><em>The budget counted towels.</em></p>
            <p className="calc-closing-line"><em>The staffing model counted hours.</em></p>
            <p className="calc-closing-line"><em>Nobody counted what it costs to care.</em></p>
            <p className="calc-closing-final">That is the calculus of care.<br />It was always chemistry.</p>
          </section>

          {/* Signature */}
          <div className="calc-signature reveal">
            <p><strong>Nexus Concordat Inc.</strong></p>
            <p>AETHER: Adaptive Endocrine Transformer Heads with Emotional Regulation</p>
            <div className="patent-tags">
              <span className="patent-tag">Patent Pending: 63/939,190</span>
              <span className="patent-tag">Patent Pending: 63/962,385</span>
              <span className="patent-tag">Patent Pending: 63/988,485</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default CalculusOfCare;
